/**
 * P-02 运行概览（docs/03-UIUX.md §5）。
 *
 * 规范硬约束：**无图表装饰**。这一页的作用是「今天有没有需要我处理的事」，
 * 不是数据可视化练习。因此全部用数字 + 状态图标 + 一条水位条，
 * 库存低于阈值的行前置警示图标并直达「生成批次」。
 */
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../icons/Icon';
import { CardSkeleton, ErrorBlock } from '../components/DataState';
import { StatusMetricCard } from './dashboard/StatusMetricCard';
import { InventoryWatermarks } from './dashboard/InventoryWatermarks';
import { ValidationSummary } from './dashboard/ValidationSummary';
import { getReadiness, getStatsOverview, listInventoryWatermarks } from '../api/endpoints';
import { describeError } from '../api/client';
import { useAsync } from '../lib/useAsync';
import { cssVar, ICON_SIZE } from '../tokens';
import { formatCount } from '../lib/format';

export function DashboardPage() {
  const statsLoader = useCallback(() => getStatsOverview(), []);
  const inventoryLoader = useCallback(() => listInventoryWatermarks(), []);
  const readinessLoader = useCallback(() => getReadiness(), []);

  const stats = useAsync(statsLoader, []);
  const inventory = useAsync(inventoryLoader, []);
  const readiness = useAsync(readinessLoader, []);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">运行概览</h1>
          <p className="page-head__sub">
            当前授权状态分布、各产品发卡库存水位与近 24 小时验证结果。数字为全部产品合计。
          </p>
        </div>
      </div>

      {stats.state.kind === 'failed' ? (
        <div className="table-frame" style={{ marginBottom: 'var(--space-6)' }}>
          <ErrorBlock message={describeError(stats.state.error, '加载运行概览')} onRetry={stats.reload} />
        </div>
      ) : stats.state.kind === 'loading' ? (
        <div className="card-grid">
          <CardSkeleton lines={2} />
          <CardSkeleton lines={2} />
          <CardSkeleton lines={2} />
          <CardSkeleton lines={2} />
        </div>
      ) : (
        <>
          <div className="card-grid">
            <StatusMetricCard status="active" value={stats.state.data.licenses.active} hint="正在被客户端使用" />
            <StatusMetricCard
              status="suspended"
              value={stats.state.data.licenses.suspended}
              hint="暂停期间验证返回否定裁决"
            />
            <StatusMetricCard
              status="revoked"
              value={stats.state.data.licenses.revoked}
              hint="不可恢复，已进入吊销列表"
            />
            <StatusMetricCard
              status="expired"
              value={stats.state.data.licenses.expired}
              hint="可通过续期回到生效中"
            />
          </div>

          <div className="card-grid">
            <div className="card">
              <div className="metric__label">
                <span style={{ color: cssVar('warn'), display: 'inline-flex' }}>
                  <Icon name="triangle-alert" size={ICON_SIZE.inline} />
                </span>
                7 天内到期
              </div>
              <div className="metric__value">{formatCount(stats.state.data.licenses.expiring_in_7d)}</div>
              <div className="metric__hint">
                <Link to="/licenses?expiring=7d" className="link-button">
                  查看这批授权
                </Link>
              </div>
            </div>

            <div className="card">
              <div className="metric__label">
                <Icon name="monitor-smartphone" size={ICON_SIZE.inline} />
                活跃设备
              </div>
              <div className="metric__value">{formatCount(stats.state.data.activations.active_devices)}</div>
              <div className="metric__hint">
                近 24 小时有心跳 {formatCount(stats.state.data.activations.seen_24h)} 台
              </div>
            </div>

            <div className="card">
              <div className="metric__label">
                <span
                  style={{
                    color: cssVar((stats.state.data.open_anomalies ?? 0) > 0 ? 'warn' : 'muted'),
                    display: 'inline-flex',
                  }}
                >
                  <Icon name="circle-alert" size={ICON_SIZE.inline} />
                </span>
                待处理异常
              </div>
              <div className="metric__value">{formatCount(stats.state.data.open_anomalies ?? 0)}</div>
              <div className="metric__hint">同码多设备 / 指纹跨授权复用 / 频率突刺</div>
            </div>

            <ServiceHealthCard state={readiness.state} onRetry={readiness.reload} />
          </div>

          <ValidationSummary data={stats.state.data.validations_24h} />
        </>
      )}

      <InventoryWatermarks state={inventory.state} onRetry={inventory.reload} />
    </>
  );
}

interface ServiceHealthCardProps {
  readonly state: ReturnType<typeof useAsync<{ database: 'up' | 'down'; signer: 'up' | 'down' }>>['state'];
  readonly onRetry: () => void;
}

function ServiceHealthCard({ state, onRetry }: ServiceHealthCardProps) {
  if (state.kind === 'loading') return <CardSkeleton lines={2} />;

  if (state.kind === 'failed') {
    return (
      <div className="card">
        <div className="metric__label">
          <span style={{ color: cssVar('danger'), display: 'inline-flex' }}>
            <Icon name="octagon-x" size={ICON_SIZE.inline} />
          </span>
          服务就绪
        </div>
        <div className="metric__hint" style={{ marginTop: 'var(--space-2)' }}>
          {describeError(state.error, '就绪探针')}
        </div>
        <button type="button" className="link-button" onClick={onRetry} style={{ marginTop: 'var(--space-2)' }}>
          重试
        </button>
      </div>
    );
  }

  const allUp = state.data.database === 'up' && state.data.signer === 'up';
  return (
    <div className="card">
      <div className="metric__label">
        <span style={{ color: cssVar(allUp ? 'success' : 'danger'), display: 'inline-flex' }}>
          <Icon name={allUp ? 'shield-check' : 'octagon-x'} size={ICON_SIZE.inline} />
        </span>
        服务就绪
      </div>
      <dl className="definition-list" style={{ marginTop: 'var(--space-3)' }}>
        <div>
          <dt>数据库</dt>
          <dd style={{ color: cssVar(state.data.database === 'up' ? 'success' : 'danger') }}>
            {state.data.database === 'up' ? '可达' : '不可达'}
          </dd>
        </div>
        <div>
          <dt>签名服务</dt>
          <dd style={{ color: cssVar(state.data.signer === 'up' ? 'success' : 'danger') }}>
            {state.data.signer === 'up' ? '可达' : '不可达'}
          </dd>
        </div>
      </dl>
    </div>
  );
}
